import React from 'react';
import { observer } from 'mobx-react';

import { AccountType, IAccount } from 'dto/access-management/account-dto';
import { DeploymentGroupType, IDisplayDeploymentGroup } from 'dto/deployment/deployment-group-dto';
import {
    HorizontalStepper,
    IconTextButton,
    IFormItemValidation,
    IStepItem,
    ITreeSelectDataSource,
    Skeleton,
    showConfirmDialog,
    Typography,
} from 'ui-lib';
import { useAccountStore, useAuthStore, useStores } from 'store';

import { AssureDeploymentCreationWizard } from './assure/assure-deployment-wizard';
import { RaptorDeploymentCreationWizard } from './raptor/raptor-deployment-wizard';
import {
    createAssureDeploymentDraft,
    createRaptorDeploymentDraft,
    DeploymentFlowDraft,
} from './deployment-create.types';
import { isValidDeploymentLabel, SelectDeploymentStep } from './select-deployment-step';
import css from './deployment-create.css';
import { useDeploymentPageNavigation } from '../hooks/use-deployment-page-navigation';

const SELECT_DEPLOYMENT_STEP_CODE = 'select-deployment';
const CONFIGURE_DEPLOYMENT_STEP_CODE = 'configure-deployment';

export const DeploymentCreateWizard = observer((): JSX.Element => {
    const { navigateToDeploymentList } = useDeploymentPageNavigation();
    const accountStore = useAccountStore();
    const { currentUser } = useAuthStore();
    const { deploymentGroupsStore } = useStores();

    const [outerStepCode, setOuterStepCode] = React.useState(SELECT_DEPLOYMENT_STEP_CODE);
    const [deploymentName, setDeploymentName] = React.useState('');
    const [selectedDeploymentGroup, setSelectedDeploymentGroup] = React.useState<IDisplayDeploymentGroup>();
    const [flowDraft, setFlowDraft] = React.useState<DeploymentFlowDraft>();
    const [selectedAccount, setSelectedAccount] = React.useState<IAccount>(() =>
        currentUser.accountType === AccountType.DPU ? null : accountStore.getAccountById(currentUser.accountId),
    );

    const accountTreeDataSource = React.useMemo<ITreeSelectDataSource>(
        () => accountStore.getSelectableTreeDataSource(Object.values(AccountType), [AccountType.CLIENT, AccountType.VAR]),
        [currentUser],
    );

    const deploymentGroups = React.useMemo<IDisplayDeploymentGroup[]>(() => {
        if (!selectedAccount) return [];
        const accountUuids = (selectedAccount.subAccounts?.map((account) => account.uuid) || []).concat(selectedAccount.uuid);
        if (selectedAccount.accountType === AccountType.CLIENT) {
            accountUuids.push(selectedAccount.parent.uuid);
        }
        return deploymentGroupsStore.displayDeploymentGroups.filter((group) => accountUuids.includes(group.accountUuid));
    }, [selectedAccount, deploymentGroupsStore.displayDeploymentGroups]);

    const deploymentGroupValidation = React.useMemo<IFormItemValidation>(() => {
        if (!selectedDeploymentGroup) {
            return { status: 'success' };
        }
        if (!selectedDeploymentGroup.deviceGroupIds?.length) {
            return {
                status: 'error',
                message: 'This deployment group does not contain any device groups.',
            };
        }
        switch (selectedDeploymentGroup.type) {
            case DeploymentGroupType.ASSURE: {
                const outOfServiceSettings = selectedDeploymentGroup.outOfServiceSettings;
                if (!outOfServiceSettings?.serviceWindowStart || !outOfServiceSettings?.serviceWindowEnd) {
                    return {
                        status: 'error',
                        message: '"Out of service window" has not been set for this deployment group.',
                    };
                }
                return { status: 'success' };
            }
            case DeploymentGroupType.RAPTOR:
            default:
                return { status: 'success' };
        }
    }, [selectedDeploymentGroup]);

    const selectedDeploymentTypeSupported = selectedDeploymentGroup?.type === DeploymentGroupType.ASSURE
        || selectedDeploymentGroup?.type === DeploymentGroupType.RAPTOR;

    const nextDisabled = !selectedAccount
        || !selectedDeploymentGroup
        || !selectedDeploymentTypeSupported
        || !isValidDeploymentLabel(deploymentName.trim())
        || deploymentGroupValidation.status !== 'success';

    const activateFlow = (): void => {
        if (!selectedDeploymentGroup || !selectedDeploymentTypeSupported) return;
        setFlowDraft(
            selectedDeploymentGroup.type === DeploymentGroupType.ASSURE
                ? { type: DeploymentGroupType.ASSURE, values: createAssureDeploymentDraft() }
                : { type: DeploymentGroupType.RAPTOR, values: createRaptorDeploymentDraft() },
        );
    };

    const onDeploymentGroupChange = (deploymentGroupId: number): void => {
        const group = deploymentGroupsStore.findDeploymentGroupById(deploymentGroupId);
        if (flowDraft && (!group || group.type !== flowDraft.type)) {
            setFlowDraft(undefined);
        } else if (flowDraft?.type === DeploymentGroupType.RAPTOR) {
            setFlowDraft({
                type: DeploymentGroupType.RAPTOR,
                values: { ...flowDraft.values, selectedScheduleDate: undefined },
            });
        }
        setSelectedDeploymentGroup(group);
    };

    const onAccountChange = (accountId: number): void => {
        setSelectedAccount(accountStore.getAccountById(accountId));
        setSelectedDeploymentGroup(undefined);
        setFlowDraft(undefined);
    };

    const changeDeploymentDetails = (): void => {
        showConfirmDialog({
            title: 'Change deployment details',
            content: 'The data entered in later steps will be lost. Do you want to continue?',
            okText: 'Continue',
            cancelText: 'Cancel',
            onOk: () => setOuterStepCode(SELECT_DEPLOYMENT_STEP_CODE),
        });
    };

    const configurationContent = selectedDeploymentGroup && flowDraft && outerStepCode === CONFIGURE_DEPLOYMENT_STEP_CODE ? (
        <React.Fragment>
            <div className={css.configurationHeader}>
                <div className={css.configurationSummary}>
                    <div className={css.configurationSummaryRow}>
                        <div className={css.configurationSummaryField}>
                            <Typography.Text strong>Deployment group:</Typography.Text>
                            <Typography.Text>{selectedDeploymentGroup.label}</Typography.Text>
                        </div>
                        <div className={css.configurationSummaryField}>
                            <Typography.Text strong>Type:</Typography.Text>
                            <Typography.Text>{selectedDeploymentGroup.type}</Typography.Text>
                        </div>
                    </div>
                    <div className={css.configurationSummaryField}>
                        <Typography.Text strong>Deployment name:</Typography.Text>
                        <Typography.Text>{deploymentName.trim()}</Typography.Text>
                    </div>
                </div>
                <IconTextButton type='link' label='Change deployment details' onClick={changeDeploymentDetails} />
            </div>
            {flowDraft.type === DeploymentGroupType.ASSURE ? (
                <AssureDeploymentCreationWizard
                    account={selectedAccount}
                    deploymentGroup={selectedDeploymentGroup}
                    deploymentName={deploymentName.trim()}
                    draft={flowDraft.values}
                    onDraftChange={(values): void => setFlowDraft({ type: DeploymentGroupType.ASSURE, values })}
                />
            ) : (
                <RaptorDeploymentCreationWizard
                    account={selectedAccount}
                    deploymentGroup={selectedDeploymentGroup}
                    deploymentName={deploymentName.trim()}
                    draft={flowDraft.values}
                    onDraftChange={(values): void => setFlowDraft({ type: DeploymentGroupType.RAPTOR, values })}
                />
            )}
        </React.Fragment>
    ) : null;

    const steps: IStepItem[] = [
        {
            code: SELECT_DEPLOYMENT_STEP_CODE,
            label: 'Select deployment',
            description: 'Choose an account, deployment group, and deployment name',
            navigation: {
                next: {
                    hidden: nextDisabled,
                    before: activateFlow,
                },
            },
            content: (
                <Skeleton loading={deploymentGroupsStore.dataLoading}>
                    <SelectDeploymentStep
                        currentUserAccountType={currentUser.accountType}
                        accountTreeDataSource={accountTreeDataSource}
                        selectedAccount={selectedAccount}
                        deploymentGroups={deploymentGroups}
                        selectedDeploymentGroup={selectedDeploymentGroup}
                        deploymentName={deploymentName}
                        deploymentGroupValidation={deploymentGroupValidation}
                        onAccountChange={onAccountChange}
                        onDeploymentGroupChange={onDeploymentGroupChange}
                        onDeploymentNameChange={setDeploymentName}
                    />
                </Skeleton>
            ),
        },
        {
            code: CONFIGURE_DEPLOYMENT_STEP_CODE,
            label: 'Configure deployment',
            description: 'Complete the deployment-type-specific workflow',
            content: configurationContent,
        },
    ];

    return (
        <HorizontalStepper
            items={steps}
            currentStepCode={outerStepCode}
            actions={{
                canGoBack: true,
                hidden: outerStepCode === CONFIGURE_DEPLOYMENT_STEP_CODE,
                cancel: {
                    onClick: navigateToDeploymentList,
                },
                finish: {
                    hidden: true,
                },
            }}
            onStepCodeChange={(_, current): void => setOuterStepCode(current?.code || SELECT_DEPLOYMENT_STEP_CODE)}
        />
    );
});
